import type { InsightsSavedView } from '#services/api/saved-views.ts';

import * as React from 'react';
import { Dialog } from '@mend/mui';
import { Button, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { savedViews } from '#lib/react-query/queries.ts';
import { deleteSavedView } from '#services/api/saved-views.ts';

interface DeleteViewModalProps {
  view: InsightsSavedView;
  onDelete: () => void;
  onClose: () => void;
}
export function SavedViewDeleteDialog(props: DeleteViewModalProps) {
  const titleId = React.useId();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const mutation = useMutation({
    mutationFn: deleteSavedView,
    onSuccess: () => {
      enqueueSnackbar('View deleted', { variant: 'success' });
      props.onDelete();
      props.onClose();
      void queryClient.invalidateQueries({ queryKey: savedViews.queryKey });
    },
    onError: () => {
      enqueueSnackbar('Failed to delete view', { variant: 'error' });
    },
  });

  return (
    <Dialog.Root open onClose={props.onClose} maxWidth="xs" fullWidth aria-labelledby={titleId}>
      <Dialog.Header>
        <Dialog.Title id={titleId}>Delete view</Dialog.Title>
      </Dialog.Header>
      <Dialog.Content>
        <Typography>
          Are you sure you want to delete{' '}
          <Typography component="span" variant="inherit" fontWeight={600}>
            {props.view.name}
          </Typography>
          ? This action cannot be undone.
        </Typography>
      </Dialog.Content>
      <Dialog.Footer>
        <Dialog.DismissButton />
        <Button
          variant="contained"
          color="error"
          size="large"
          onClick={() => mutation.mutate(props.view.id)}
          disabled={mutation.isPending}
        >
          Delete
        </Button>
      </Dialog.Footer>
    </Dialog.Root>
  );
}
